import { InMemoryCache } from "@apollo/client";
import { mergeLiveTimeSeriesChange } from "../utils/mergeLiveTimeSeriesChange.js";
const mergeTimeSeriesField = (existing, incoming) => mergeLiveTimeSeriesChange(existing, incoming) ?? incoming;
/**
 * Offset-positioned merge for the paginated `listLeaderboard.edges` array,
 * extracted from the field policy below so it can be unit-tested directly.
 *
 * Each incoming page is written at its `offset`, so the single shared cache
 * entry always holds the full accumulated list (a plain replace would leave
 * only the last page). The result is then clamped to `totalCount` — the
 * server reports the full current list size on every page — so a refreshed
 * page-zero response that returns a shorter list drops stale trailing rows
 * from an earlier, longer snapshot instead of leaving phantom entries beyond
 * the new end of the list.
 */
export function mergeLeaderboardEdges(existingEdges, incomingEdges, offset, totalCount) {
    const merged = existingEdges.slice(0);
    for (let i = 0; i < incomingEdges.length; i += 1) {
        merged[offset + i] = incomingEdges[i];
    }
    return typeof totalCount === "number" ? merged.slice(0, totalCount) : merged;
}
export const createCache = () => new InMemoryCache({
    // Required for any named fragment whose type condition is a UNION (or
    // interface): without this map the cache cannot prove e.g.
    // `Trade ⊆ SignalItem`, silently drops every field selected under
    // `fragment ... on SignalItem` at read time, and components receive
    // `activityItem: { __typename: "Trade" }` with no data (the For You feed
    // rendered all rows as "Sell $0.00 / 0s" this way — PR #2630 moved the
    // activity selections from inline `... on Trade` spreads, which match by
    // exact typename and need no config, into a named union fragment).
    // Members must match `union SignalItem` in
    // domains/orby/src/api/graphql/schemas/social_trading.ts.
    possibleTypes: {
        SignalItem: ["Trade", "PerpTrade", "PredictionTradeItem", "PredictionOrder", "SignalCluster"],
    },
    typePolicies: {
        Query: {
            fields: {
                getPerpOrderbook: {
                    keyArgs: ["input", ["standardizedInstrumentId", "aggregation"]],
                },
                // Key positions only by accountClusterId so that:
                // 1. Changing meshBalances doesn't create a separate cache entry
                // 2. LiveUpdatesProvider (which omits meshBalances) reads/writes the same entry
                getPositions: {
                    keyArgs: [
                        "input",
                        ["accountClusterId", "walletAddress", "status", "instrumentFilter", "sortBy", "timeframe"],
                    ],
                },
                getPredictionOrderbook: {
                    keyArgs: ["input", ["standardizedInstrumentId", "marketId", "outcomeTokenId"]],
                },
                getWaitListStatus: {
                    keyArgs: ["input", ["userId"]],
                },
                listStandardizedTokensByCategory: {
                    keyArgs: ["input"],
                    merge(_existing, incoming) {
                        return incoming;
                    },
                },
                // Cache the leaderboard separately per (instrumentType, timeInterval)
                // so switching pills doesn't bleed rows from a previous slice into
                // the new view, and `fetchMore({ offset: N })` shares a slot with
                // the initial offset=0 observation.
                //
                // `edges` merge is offset-positioned (the standard Apollo
                // offset-pagination strategy): each incoming page is written at its
                // `offset`, so the single shared entry always holds the full
                // accumulated list. A replace strategy is WRONG here -- it leaves the
                // entry holding only the last page fetched, so when a consumer
                // remounts (e.g. navigating to a profile and back) and re-observes
                // `offset:0` with `cache-and-network`, the cache hands it that
                // stale mid-list page and the list appears to start at rank > 1.
                // Switching pills is unaffected: `keyArgs` already routes each
                // (instrumentType, timeInterval) slice to its own entry.
                listLeaderboard: {
                    keyArgs: ["input", ["instrumentType", "timeInterval"]],
                    merge(existing, incoming, { args }) {
                        const offset = args?.input?.offset ?? 0;
                        return {
                            ...incoming,
                            edges: mergeLeaderboardEdges(existing?.edges ?? [], incoming?.edges ?? [], offset, incoming?.pageInfo?.totalCount),
                        };
                    },
                },
                // Signal feed pagination. `keyArgs` deliberately excludes `limit`
                // and `offset` so every `fetchMore({ offset: N })` writes into the
                // SAME cache entry the hook observes at `offset:0` — without this,
                // each page lands under a distinct storeFieldName, the observed
                // query never sees it, and `fetchMore` is a silent no-op (the feed
                // stays frozen at page one and infinite scroll spins forever).
                // `followingOnly` and `instrumentTypes` are BOTH in the key so the
                // global / following-only feeds and the per-instrument tabs
                // (All/Tokens/Perps/Predictions) each get an isolated page cache and
                // don't bleed into each other. Without `instrumentTypes` in the key,
                // switching tabs would synchronously replay the previous tab's
                // cached page into the consumer's local `items` (which only
                // dedupes/appends, never removes), leaving stale non-matching rows
                // until a full remount.
                //
                // `merge` is a plain replace (NOT offset-positioned like
                // `listLeaderboard`): `useListSignalsQuery`'s consumers
                // (`ForYouRail` / `ForYouFeed`) accumulate, dedupe, and time-sort
                // pages into their own local state and also prepend live
                // subscription rows there. The cache hands them one page at a time;
                // the component is the single accumulator.
                //
                // Replace-merge means the slot holds only the LAST fetched page —
                // including its `pageInfo.hasNextPage`. Any policy that lets a
                // revisit be served purely from this slot (e.g. plain cache-first)
                // strands the feed on a stale mid-feed page and, if that page was
                // terminal, freezes infinite scroll. `useListSignalsQuery` therefore
                // pairs cache-and-network (mount / variables change) with a
                // cache-first nextFetchPolicy — see the regression test in
                // `__tests__/graphql/listSignalsFetchPolicy.test.ts`.
                listSignals: {
                    keyArgs: ["input", ["followingOnly", "instrumentTypes", "feedMode", "includeClusters", "minNotionalUsd"]],
                    merge(_existing, incoming) {
                        return incoming;
                    },
                },
            },
        },
        Asset: {
            keyFields: ["symbol"],
        },
        CategoryMetadata: {
            keyFields: false,
        },
        Currency: {
            keyFields: false,
        },
        FungibleToken: {
            keyFields: false,
        },
        GetWaitListStatusResponse: {
            keyFields: false,
        },
        StandardizedFungibleToken: {
            keyFields: ["id", "standardizedInstrumentId"],
            fields: {
                currentPrice: { merge: mergeTimeSeriesField },
                currentMarketCap: { merge: mergeTimeSeriesField },
                currentTotalVolume: { merge: mergeTimeSeriesField },
                fungibleTokens: {
                    merge(_existing, incoming) {
                        return incoming;
                    },
                },
            },
        },
        StandardizedPerpetual: {
            keyFields: ["standardizedInstrumentId"],
            fields: {
                currentPrice: { merge: mergeTimeSeriesField },
                currentTotalVolume: { merge: mergeTimeSeriesField },
            },
        },
        StandardizedPredictionEvent: {
            keyFields: ["id", "standardizedInstrumentId"],
            fields: {
                volume: { merge: mergeTimeSeriesField },
            },
        },
        StandardizedTokensByCategory: {
            keyFields: ["category"],
            fields: {
                standardizedTokens: {
                    merge(_existing, incoming) {
                        return incoming;
                    },
                },
            },
        },
        SubcategoryMetadata: {
            keyFields: ["__typename", "clientCacheKey"],
        },
    },
});
